/**
 * Decides whether to show the app or the login screen.
 *
 * Wraps the app rather than living inside it, so no data component ever has to
 * think about auth — by the time `App` mounts, there is a session.
 *
 * When the server runs without credentials configured, this is transparent:
 * `required: false` and the app renders straight through.
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { api } from "../api.ts";
import { LoginScreen } from "./LoginScreen.tsx";

type Status = "checking" | "signed-in" | "signed-out";

export function AuthGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("checking");

  const check = useCallback(async () => {
    try {
      const session = await api.session();
      setStatus(session.authenticated ? "signed-in" : "signed-out");
    } catch {
      // The session endpoint is unauthenticated, so a failure here means the
      // server is unreachable — not that the reader is signed out. Showing the
      // app lets its own error handling explain what is wrong.
      setStatus("signed-in");
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  // A blank frame rather than a flash of the login form, which would appear
  // for a moment on every reload even when signed in.
  if (status === "checking") {
    return <div className="min-h-dvh bg-background" aria-busy="true" />;
  }

  if (status === "signed-out") {
    return <LoginScreen onSignedIn={() => setStatus("signed-in")} />;
  }

  return <>{children}</>;
}
